import _extends from "@babel/runtime/helpers/extends";
/*
 *                     NOTICE
 *            COPYRIGHT 2022 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */
import { SelectionMode } from "abreact/incubator/ui/SharedTypes";
import { KeyboardUtilsReact } from "abreact/incubator/ui/utils/KeyboardUtilsReact";
import { ReactUtils } from "abreact/incubator/ui/utils/ReactUtils";
import { AB_INDETERMINATE_VALUE, AbCheckbox } from "abreact/input/AbCheckbox";
import { AbList } from "abreact/input/AbList";
import { AbButton } from "abreact/misc/AbButton";
import { AbIcon } from "abreact/misc/AbIcon";
import { AbItemUtils } from "abreact/misc/AbItemUtils";
import { AbSize } from "abreact/misc/AbSize";
import { AbBusyIndicator, BusyIndicatorSize } from "abreact/progress/AbBusyIndicator";
import { AbEllipsisAnimation } from "abreact/progress/AbEllipsisAnimation";
import { LOADING, NOT_FOUND, SEPARATOR, toKey } from "abreact/tree/AbTreeListItem";
import { AbTreeEvent, AbTreeUtils } from "abreact/tree/AbTreeUtils";
import { AbTreeRootNodeImpl } from "abreact/tree/node/AbTreeRootNodeImpl";
import * as AbHooks from "abreact/utils/AbHooks";
import { invokeRef, useSynchronizedCallback } from "abreact/utils/AbHooks";
import { AbSelectionPropsUtils } from "abreact/utils/AbSelectionPropsUtils";
import { TypeToJumpUtils } from "abreact/utils/TypeToJumpUtils";
import { AbEnriched } from "abreact/utils/enrichment/AbEnriched";
import { AbIterable } from "abweb/collection/AbIterable";
import { AbListHelper } from "abweb/collection/AbListHelper";
import { AbSet } from "abweb/collection/AbSet";
import { ArrayUtils } from "abweb/utils/ArrayUtils";
import { Assert } from "abweb/utils/Assert";
import { ClassUtils } from "abweb/utils/ClassUtils";
import { KeyValue } from "abweb/utils/KeyboardUtils";
import { TypeUtils } from "abweb/utils/TypeUtils";
import { LogUtils } from "abweb/utils/log/LogUtils";
import * as React from "react";
export let AbTreeType = /*#__PURE__*/function (AbTreeType) {
  /**
   * Both the default and recommended. This makes the value and onChange
   * props use full paths- ItemT[]. Requires that each node in the tree has a unique
   * key among its siblings.
   */
  AbTreeType[AbTreeType["PATH"] = 0] = "PATH";
  /**
   * Uses only the ItemT of the node for value and onChange. Opts
   * out of auto-expand to value behavior. Requires that each node in the tree
   * has a unique key for the entire tree.
   */
  AbTreeType[AbTreeType["UNIQUE"] = 1] = "UNIQUE";
  return AbTreeType;
}({});
export let AbTristateType = /*#__PURE__*/function (AbTristateType) {
  /**
   * How changing a checkbox affects the state of other checkboxes.
   * <b>independent</b> - a checkbox has no effect on other checkboxes.
   *     Unchecked nodes with a checked descendant are rendered "indeterminate".
   *     Unchecked nodes with no checked descendants are rendered "unchecked".
   *     Checked nodes are always rendered "checked", regardless of the state of the descendants.
   *
   * <b>dependent</b> - a checkbox controls and is controlled by the checkboxes in all descendants.
   *     Setting the box checked makes all descendants checked.
   *     Setting the box unchecked makes all the descendants unchecked.
   *
   *     Nodes where all of the descendants are checked are rendered "checked".
   *     Nodes where all of the descendants are unchecked are rendered "unchecked".
   *     Nodes where some of the descendants are checked and some are unchecked are rendered "indeterminate".
   *     Clicking on an indeterminate checkbox sets it checked (as well as its descendants).
   */
  AbTristateType[AbTristateType["DEPENDENT"] = 0] = "DEPENDENT";
  AbTristateType[AbTristateType["INDEPENDENT"] = 1] = "INDEPENDENT";
  return AbTristateType;
}({});

// todo find a way to not have so many list-related functions here -- used in AbFilteredTree to directly manipulate list for vertical arrows moving focus to/from
// Also tried adding a listRef inside the tree ref, but was running into problems

const LOGGER = LogUtils.getLogger(__filename);
/**
 * Internal common implementation for several types of trees.
 */
const AbTreeList = props => {
  const listRef = React.useRef(null);
  const [freshness, setFreshness] = React.useState(0);
  const treeRootNode = useTreeRootNode(props.roots, props.keyFunction);
  const uniqueItemIdPrefix = AbHooks.useUniqueId();

  // Keep callbacks in sync
  React.useEffect(() => {
    if (props.onExpand) {
      return treeRootNode.subscribe(AbTreeEvent.EXPAND, props.onExpand);
    }
  }, [props.onExpand, treeRootNode]);
  React.useEffect(() => {
    if (props.onCollapse) {
      return treeRootNode.subscribe(AbTreeEvent.COLLAPSE, props.onCollapse);
    }
  }, [props.onCollapse, treeRootNode]);
  React.useEffect(() => {
    return treeRootNode.subscribe(AbTreeEvent.UPDATE, () => {
      setFreshness(n => n + 1);
    });
  }, [treeRootNode]);
  const items = React.useRef({
    get size() {
      return treeRootNode.size;
    },
    get itemAt() {
      return treeRootNode.itemAt;
    },
    // Unique trees can't handle indexOf
    get indexOf() {
      return props.type === AbTreeType.UNIQUE ? undefined : treeRootNode.indexOf;
    },
    get keyFunction() {
      return props.treeItemKeyFunction;
    }
  }).current;
  const [listScrollIndex, setListScrollIndex] = React.useState(undefined);
  const didExpandInitialization = React.useRef(false);
  if (!didExpandInitialization.current) {
    didExpandInitialization.current = true;
    let valueArray = [];
    treeRootNode.doBatchAction(() => {
      if (props.type === AbTreeType.UNIQUE) return;
      if (props.selectionMode === SelectionMode.SINGLE) {
        if (props.value === undefined) return;
        const item = AbEnriched.getItem(props.value);
        valueArray = [item];
        return AbTreeUtils.expandPath(treeRootNode, item.path.slice(0, -1));
      } else if (props.selectionMode === SelectionMode.MULTIPLE) {
        if (props.value.size === 0) return;
        valueArray = [...props.value];
        const paths = valueArray.map(item => AbEnriched.getItem(item).path);
        return AbTreeUtils.expandPaths(treeRootNode, paths.map(path => path.slice(0, -1)));
      }
    }, true)?.then(() => {
      console.log("doing scroll", valueArray);
      if (valueArray.length === 0) return;
      const indices = valueArray.map(item => treeRootNode.indexOf(item));
      setListScrollIndex(Math.min(...indices));
    });
  }
  AbHooks.useOnValueChange(() => {
    if (props.keyFunction) LOGGER.warn("AbTree does not support changing the keyFunction prop");
  }, props.keyFunction);

  // If the dataFreshness prop changes, refresh the tree roots and re-render the list
  AbHooks.useOnValueChange(() => {
    treeRootNode.refresh();
  }, props.dataFreshness);
  const onKeyDown = KeyboardUtilsReact.makeKeyHandler([[KeyValue.ARROW_LEFT, () => {
    const index = AbTreeUtils.treeLeftArrow(treeRootNode, listRef.current?.getFocusItem()?.[1]);
    if (index === undefined) return;
    const item = AbEnriched.getItem(treeRootNode.itemAt(index));
    if (!item || item.value === SEPARATOR) return;
    listRef.current?.setFocusItem([index, item]);
  }], [KeyValue.ARROW_RIGHT, () => {
    const focusItem = listRef.current?.getFocusItem();
    if (!focusItem || !focusItem[1].expander) return;
    if (!focusItem[1].expander.isExpanded()) {
      focusItem[1].expander.expand(); // If the current focus is collapsed, expand it.
    } else {
      // Move focus down.
      listRef.current?.incrementSelection(1);
    }
  }], [KeyValue.HOME, () => {
    listRef.current?.focusFirstFocusable();
  }], [KeyValue.END, () => {
    listRef.current?.focusLastFocusable();
  }], ["*", () => {
    const focusItem = listRef.current?.getFocusItem();
    if (!focusItem) return;
    AbTreeUtils.expandAllSiblings(treeRootNode, focusItem[1]);
  }]]);
  const treeValueRenderer = (item, enrichment) => {
    Assert.assert(item.value !== SEPARATOR);
    if (item.value === LOADING) return /*#__PURE__*/React.createElement(AbBusyIndicator, {
      size: BusyIndicatorSize.TINY
    });
    if (item.value === NOT_FOUND || !item.path) return /*#__PURE__*/React.createElement(AbIcon, {
      image: "absdk:error"
    }); // Should NEVER happen, is used if tree functionality fails.
    if (props.itemRenderer) return props.itemRenderer(item.value, enrichment, {
      path: item.path,
      enrichedPath: item.enrichedPath
    });
    return AbItemUtils.defaultItemDisplayName(item.value, enrichment);
  };
  const treeExpandRenderer = item => {
    Assert.assert(item.value !== SEPARATOR);
    const expander = item.expander;
    if (!expander) return /*#__PURE__*/React.createElement("div", {
      className: "ab-tree-expand-spacer"
    });
    const toggle = () => {
      expander.isExpanded() ? expander.collapse() : expander.expand();
    };
    return /*#__PURE__*/React.createElement("div", {
      className: "ab-tree-expand-wrapper"
    }, /*#__PURE__*/React.createElement(AbButton, {
      icon: expander.isExpanded() ? "absdk:chevron-down" : "absdk:chevron-right",
      eventHandlers: {
        onKeyDown: KeyboardUtilsReact.makeKeyHandler([[[KeyValue.ENTER, KeyValue.SPACE], toggle]]),
        onDoubleClick: e => {
          e.stopPropagation();
        }
      },
      size: AbSize.MEDIUM,
      tabIndex: -1,
      className: "ab-no-border-button ab-tree-expand ab-icon-button",
      onClick: e => {
        e.stopPropagation();
        toggle();
        listRef.current?.setFocusItem([item.index, item]);
      }
    }));
  };
  const treeListItemRenderer = AbHooks.useCurrentCallback((item, enrichment) => {
    if (item.value === SEPARATOR) {
      return /*#__PURE__*/React.createElement(React.Fragment, null, makeSpacers(item.depth - 1 + 2, "separator"), /*#__PURE__*/React.createElement("hr", {
        className: "ab-tree-list-separator"
      }));
    }
    const itemKey = props.treeItemKeyFunction(item);
    const isSelected = props.selectionMode === SelectionMode.MULTIPLE ? AbIterable.any(Array.from(props.value, AbEnriched.getItem), v => props.treeItemKeyFunction(v) === itemKey) : false;
    const isIndeterminate = props.indeterminateValueKeys?.has(itemKey);
    const getCheckboxValue = () => {
      if (isSelected) return true;
      if (isIndeterminate) return AB_INDETERMINATE_VALUE;
      return false;
    };
    const getAriaOwns = () => {
      if (!item.expander?.isExpanded()) return undefined;
      return AbTreeUtils.indicesOfChildren(treeRootNode, item.path)?.map(index => `${uniqueItemIdPrefix}-${index}`).join(" ");
    };
    return /*#__PURE__*/React.createElement(React.Fragment, null, makeSpacers(item.depth - 1, "element"), treeExpandRenderer(item), props.selectionMode === SelectionMode.MULTIPLE && !enrichment?.displayOnly && /*#__PURE__*/React.createElement(AbCheckbox, {
      className: "ab-tree-list-item-checkbox",
      tabIndex: -1,
      value: getCheckboxValue(),
      disabled: enrichment?.disabled || props.disabled,
      onChange: () => {
        // noop
      }
    }), treeValueRenderer(item, enrichment), /*#__PURE__*/React.createElement("div", {
      className: "ab-visually-hidden ab-tree-group",
      role: "group",
      "aria-owns": getAriaOwns()
    }), /*#__PURE__*/React.createElement("span", {
      style: {
        display: "none"
      },
      "data-ab-tree-item-key": itemKey
    }));
  });

  // todo when an item renderer is provided, disable default type-to-focus string matching
  //  (while preserving it for multiselect rich dropdowns, which render checkboxes)
  const defaultTypeToFocusItemToString = TypeToJumpUtils.defaultItemToString;
  const typeToFocusProps = {
    enabled: props.__typeToFocusProps?.enabled,
    initialFocusString: props.__typeToFocusProps?.initialFocusString,
    itemToString: treeListItem => {
      // for type-to-focus to work on trees with items lacking a displayName, we need to unwrap the value
      const value = AbEnriched.getItem(treeListItem).value;
      return defaultTypeToFocusItemToString(AbEnriched.createMaybe(value, AbEnriched.getEnrichment(treeListItem)));
    }
  };
  const commonProps = {
    items: items,
    name: props.name,
    tooltip: props.tooltip,
    disabled: props.disabled,
    tabIndex: props.tabIndex,
    autoFocus: props.autoFocus,
    ...ReactUtils.componentProps("ab-tree", props),
    scrollToIndex: props.scrollToIndex ?? listScrollIndex,
    dataTestId: props.dataTestId,
    keyFunction: props.treeItemKeyFunction,
    itemRenderer: treeListItemRenderer,
    dataFreshness: freshness,
    virtualized: props.virtualized,
    size: props.size,
    itemIdPrefix: uniqueItemIdPrefix,
    itemEventHandlers: {
      onClick: mapHandler(props.itemEventHandlers?.onClick),
      onDoubleClick: (item, event) => {
        mapHandler(props.itemEventHandlers?.onDoubleClick)?.(item, event);
        // Disable double click to expand/collapse on multi-select. Decided by UX team due to MH-35806
        if (props.selectionMode === SelectionMode.MULTIPLE) return;
        const expander = item.expander;
        if (expander && !event.defaultPrevented) {
          expander.isExpanded() ? expander.collapse() : expander.expand();
        }
      },
      onDrag: mapHandler(props.itemEventHandlers?.onDrag),
      onDragStart: mapHandler(props.itemEventHandlers?.onDragStart),
      onDragEnd: mapHandler(props.itemEventHandlers?.onDragEnd),
      onKeyDown: mapHandler(props.itemEventHandlers?.onKeyDown)
    },
    __htmlProps: {
      onKeyDown: e => {
        props.__htmlProps?.onKeyDown?.(e);
        onKeyDown(e);
      },
      role: "tree"
    },
    onSelect: props.onSelect ? v => {
      !!v.path && props.onSelect?.(v.path);
    } : undefined,
    onUnselect: props.onUnselect ? v => {
      !!v.path && props.onUnselect?.(v.path);
    } : undefined,
    abRef: ReactUtils.mergeRefs(listRef, newListRef => {
      if (!props.abRef) return;
      if (!newListRef) {
        invokeRef(props.abRef, null);
      } else {
        invokeRef(props.abRef, {
          root: treeRootNode,
          scrollToItem: path => {
            if (!listRef.current) return false;
            const index = AbTreeUtils.indexOf(treeRootNode, path);
            if (index === undefined) return false;
            listRef.current.scrollToIndex(index);
            return true;
          },
          isFirstFocused: newListRef.isFirstFocused,
          focusFirstFocusable: newListRef.focusFirstFocusable,
          scrollToIndex: newListRef.scrollToIndex
        });
      }
    }),
    __typeToFocusProps: typeToFocusProps
  };
  switch (props.selectionMode) {
    case SelectionMode.MULTIPLE:
      return /*#__PURE__*/React.createElement(AbList, _extends({}, commonProps, {
        selectionMode: SelectionMode.MULTIPLE,
        value: props.value,
        onChange: (value, maybeEnrichedValue) => {
          const newValueSet = AbSet.from(value.filterType(item => !!item.path));
          const newMaybeEnrichedValueSet = AbSet.from(maybeEnrichedValue.filterType(item => !!AbEnriched.getItem(item).path));
          props.onChange(newValueSet, newMaybeEnrichedValueSet);
        }
      }));
    case SelectionMode.SINGLE:
      return /*#__PURE__*/React.createElement(AbList, _extends({}, commonProps, {
        selectionMode: SelectionMode.SINGLE,
        value: props.value,
        onChange: (value, maybeEnrichedValue) => {
          if (value.path) {
            // convince TS this is not an AbTreeListItemInternal
            const reEnriched = AbEnriched.createMaybe(value, AbEnriched.getEnrichment(maybeEnrichedValue));
            props.onChange(value, reEnriched);
          }
        }
      }));
    case SelectionMode.NONE:
    case undefined:
      return /*#__PURE__*/React.createElement(AbList, commonProps);
  }
};

/**
 * Recommended Tree component. Supports path-based, unique, no-select, single-select, multi-select.
 */
export const AbTree = props => {
  if (props.type === AbTreeType.UNIQUE) {
    const listKeyFunction = item => {
      if (item.path) {
        // Note: this does not return a unique key, as a tree can have the same item shown in multiple branches.
        // We might want to change AbList so that it doesn't use the key as the React key, since this will then
        // mean that there are duplicates.
        return TypeUtils.itemToKey(item.value, props.keyFunction);
      }
      return toKey(item);
    };
    const itemToListItem = item => {
      return {
        path: [],
        // Note: path is required, but it is only needed for items shown in the list
        enrichedPath: [],
        value: item,
        depth: -1,
        index: -1
      };
    };
    const commonProps = {
      ...AbSelectionPropsUtils.removeSelectionProps(props),
      ...ReactUtils.componentProps("ab-tree", props),
      treeItemKeyFunction: listKeyFunction,
      onSelect: props.onSelect,
      onUnselect: props.onUnselect
    };
    const selectionProps = AbSelectionPropsUtils.mapSelectionProps(props, itemToListItem, item => item.value);
    return /*#__PURE__*/React.createElement(AbTreeList, _extends({}, commonProps, selectionProps, {
      keyFunction: props.keyFunction
    }));
  }
  const {
    pathKeyFunction,
    listKeyFunction,
    pathToListItem
  } = getPathHelpers(props.keyFunction);
  const commonProps = {
    ...AbSelectionPropsUtils.removeSelectionProps(props),
    ...ReactUtils.componentProps("ab-tree", props),
    treeItemKeyFunction: listKeyFunction,
    onSelect: props.onSelect,
    onUnselect: props.onUnselect
  };
  const selectionProps = AbSelectionPropsUtils.mapSelectionProps({
    ...props,
    keyFunction: pathKeyFunction
  }, pathToListItem, item => item.path);
  return /*#__PURE__*/React.createElement(AbTreeList, _extends({}, commonProps, selectionProps, {
    keyFunction: props.keyFunction
  }));
};
const getPathHelpers = keyFunction => {
  const pathKeyFunction = AbTreeUtils.makePathKeyFunction(keyFunction);
  const listKeyFunction = item => item.path ? pathKeyFunction(item.path) : toKey(item);
  const pathToListItem = path => {
    return {
      path: path,
      enrichedPath: path,
      value: path[path.length - 1],
      depth: -1,
      index: -1
    };
  };
  return {
    pathKeyFunction,
    listKeyFunction,
    pathToListItem
  };
};

/**
 * Tristate tree implementation for when selection is more tied to the structure of the tree data.
 * For example, a selected child in a tristate tree makes its ancestors have an indeterminate
 * selection state.
 */
export const AbTristateTree = props => {
  const {
    value,
    onChange,
    roots,
    ...rest
  } = props;
  const rootNode = useTreeRootNode(roots, props.keyFunction);
  Assert.assert(ClassUtils.isInstanceOf(rootNode, AbTreeRootNodeImpl));
  const [neverCached, setNeverCached] = React.useState(true);
  const [cacheTree, treeCaching] = useSynchronizedCallback(() => AbTreeUtils.cacheAll(rootNode));
  React.useEffect(() => {
    cacheTree();
    setNeverCached(false);
  }, [props.dataFreshness, props.roots]);
  if (treeCaching || neverCached) return /*#__PURE__*/React.createElement(AbEllipsisAnimation, null);
  const {
    pathKeyFunction,
    listKeyFunction,
    pathToListItem
  } = getPathHelpers(props.keyFunction);
  const valuePaths = (() => {
    if (props.treeType === AbTreeType.UNIQUE) {
      // Need to find all the paths for the unique values
      return AbTreeUtils.itemsToNodes(rootNode, props.value).map(node => AbEnriched.createMaybe(node.path, node.enrichment));
    }
    return props.value;
  })();
  const commonProps = {
    ...rest,
    ...ReactUtils.componentProps("ab-tree", props),
    treeItemKeyFunction: listKeyFunction
  };
  if (props.tristateType === AbTristateType.INDEPENDENT) {
    const indeterminateNodeKeys = AbTreeUtils.getIndependentIndeterminateNodes(rootNode, valuePaths, node => pathKeyFunction(node.path));
    const selectionProps = AbSelectionPropsUtils.mapSelectionProps({
      value: valuePaths,
      onChange: (paths, enrichedPaths) => {
        if (props.treeType === AbTreeType.UNIQUE) {
          props.onChange(paths.map(path => path[path.length - 1]), enrichedPaths.map(enrichedPath => {
            const {
              item,
              enrichment
            } = AbEnriched.extract(enrichedPath);
            return AbEnriched.createMaybe(item[item.length - 1], enrichment);
          }));
        } else {
          props.onChange(paths, enrichedPaths);
        }
      },
      selectionMode: SelectionMode.MULTIPLE,
      keyFunction: pathKeyFunction
    }, pathToListItem, item => item.path);
    return /*#__PURE__*/React.createElement(AbTreeList, _extends({}, commonProps, selectionProps, {
      roots: rootNode,
      keyFunction: props.keyFunction,
      indeterminateValueKeys: indeterminateNodeKeys
    }));
  }
  const {
    indeterminateNodeKeys,
    valueNodes
  } = AbTreeUtils.getTristateSets(rootNode, valuePaths, node => pathKeyFunction(node.path));
  const selectionProps = AbSelectionPropsUtils.mapSelectionProps({
    value: valueNodes.map(node => AbEnriched.create(node.path, node.enrichment)),
    onChange: (paths, _) => {
      const newlyUnselected = valueNodes.filter(node => !paths.has(node.path));
      const filteredPaths = paths.filter(maybeEnrichedPath => {
        // Remove if any newly unselected path is a subpath
        const path = AbEnriched.getItem(maybeEnrichedPath);
        return !newlyUnselected.any(node => isSubpath(path, node.path) || isSubpath(node.path, path));
      });
      const {
        valueNodes: withParentsHandled
      } = AbTreeUtils.getTristateSets(rootNode, filteredPaths, node => pathKeyFunction(node.path));
      if (props.treeType === AbTreeType.UNIQUE) {
        props.onChange(withParentsHandled.map(node => node.value), withParentsHandled.map(node => AbEnriched.createMaybe(node.value, node.enrichment)));
      } else {
        props.onChange(withParentsHandled.map(node => node.path), withParentsHandled.map(node => AbEnriched.createMaybe(node.path, node.enrichment)));
      }
    },
    selectionMode: SelectionMode.MULTIPLE,
    keyFunction: pathKeyFunction
  }, pathToListItem, item => item.path);
  return /*#__PURE__*/React.createElement(AbTreeList, _extends({}, commonProps, selectionProps, {
    roots: rootNode,
    keyFunction: props.keyFunction,
    indeterminateValueKeys: indeterminateNodeKeys
  }));
};

/**
 * Helper hook for keeping an AbTreeRootNode in sync with the roots prop. WARNING: Must be before callback effects
 * such as React.useEffect.
 */
export function useTreeRootNode(roots, keyFunction) {
  const [treeRootNode, setTreeRootNode] = React.useState(() => {
    if (areRootsRawData(roots)) {
      return new AbTreeRootNodeImpl(roots, keyFunction);
    }
    if (keyFunction != null && keyFunction !== roots.keyFunction) {
      LOGGER.warn("Ignoring provided keyFunction in favor of keyFunction in provided AbTreeRootNode");
    }
    return roots;
  });

  /**
   * Refresh tree if roots change. MUST BE DONE before callback effects such as React.useEffect.
   */
  AbHooks.useOnValueChange(() => {
    if (areRootsRawData(roots)) {
      /** New array of data */
      treeRootNode.refresh(roots);
    } else {
      /** Roots have changed to a new {@link AbTreeRootNode} */
      setTreeRootNode(roots);
    }
  }, roots);
  return treeRootNode;
}
const makeSpacers = (num, keyPrefix) => {
  return AbListHelper.range(0, num).map(i => /*#__PURE__*/React.createElement("div", {
    className: "ab-tree-spacer",
    key: `${keyPrefix}-spacer-${i}`
  })).toArray();
};
function areRootsRawData(roots) {
  return ArrayUtils.isArrayLike(roots) || typeof roots === "function";
}
const mapHandler = f => {
  if (!f) return undefined;
  return (item, event) => {
    item.path && f(item.path, event);
  };
};
const isSubpath = (p1, p2, keyFunction) => {
  if (p1.length > p2.length) return false;
  for (let i = 0; i < p1.length; i++) {
    if (!TypeUtils.equals(p1[i], p2[i], keyFunction)) return false;
  }
  return true;
};
//# sourceMappingURL=AbTree.js.map
